import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import CheckRow from './CheckRow';
import { PILLAR_LABELS, PILLAR_DESCRIPTIONS, scoreColor } from './uavsMeta';
import { useTokens } from '../primitives';

export default function PillarCard({
  pillarKey,
  pillar,
  isLocked,
  evidenceByCheck = {},
  defaultExpanded = false,
}) {
  const t = useTokens();
  const [open, setOpen] = useState(defaultExpanded);
  const pct =
    pillar.max > 0 ? Math.round((pillar.earned / pillar.max) * 100) : 0;
  const checks = (pillar.checkResults || []).filter(
    (c) => c.result !== 'skip' && c.result !== 'error'
  );

  const canExpand = !isLocked && checks.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        opacity: isLocked ? 0.6 : 1,
      }}
    >
      <Box
        onClick={canExpand ? () => setOpen((v) => !v) : undefined}
        sx={{
          p: 2,
          cursor: canExpand ? 'pointer' : 'default',
          '&:hover': canExpand ? { bgcolor: 'action.hover' } : undefined,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {isLocked && (
                <LockIcon
                  sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}
                />
              )}
              {PILLAR_LABELS[pillarKey] || pillarKey}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {PILLAR_DESCRIPTIONS[pillarKey]}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: scoreColor(pct, t) }}
            >
              {isLocked ? 'Upgrade' : `${pillar.earned}/${pillar.max}`}
            </Typography>
            {canExpand && (
              <IconButton
                size="small"
                aria-label={open ? 'Collapse' : 'Expand'}
                sx={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {!isLocked && (
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 6,
              borderRadius: 3,
              mt: 1.5,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': {
                bgcolor: scoreColor(pct, t),
                borderRadius: 3,
              },
            }}
          />
        )}
      </Box>
      {canExpand && (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ px: 2, pb: 1.5 }}>
            {checks.map((check) => (
              <CheckRow
                key={check.checkKey}
                check={check}
                evidence={evidenceByCheck[check.checkKey]}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}
